import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PrintSettings } from '../../core/settings';
import { StorageService, UploadProgressEvent } from '../../core/storage';
import { PrintFile, PrintFileStatus, PrintFileSubStatus } from '../product';
import { injectUploadApi, ProcessFileApiResponse, UpdateThumbnailApiResponse } from './upload-api';
import { UploadDbService } from './upload-db.service';

@Injectable({ providedIn: 'root' })
export class UploadService {
  private uploadApi = injectUploadApi();
  private uploadDbService = inject(UploadDbService);
  private storageService = inject(StorageService);

  /**
   * Generates a unique Firestore document ID for an upload.
   */
  createId(): string {
    return this.uploadDbService.createId();
  }

  /**
   * Uploads a file to Firebase Storage with real-time progress tracking.
   * On completion, creates a Firestore document in the specified collection
   * with the original file metadata and initial status.
   *
   * Storage path: `{collection}/{fileId}/{file.name}`
   * Firestore doc: `{collection}/{fileId}` with `originalFile` field + status metadata
   *
   * @param file - The browser File to upload.
   * @param fileId - The pre-generated Firestore document ID for this upload.
   * @param collection - The Firestore collection path (e.g. 'uploads' or 'products/{id}/uploads').
   * @returns Observable emitting progress events and, on completion, the StoredFile with firestoreId.
   */
  uploadFileWithProgress(
    file: File,
    fileId: string,
    collection: string = 'uploads',
  ): Observable<UploadProgressEvent> {
    const storagePath = `${collection}/${fileId}`;

    return this.storageService.uploadFileWithProgress(
      file,
      storagePath,
      collection,
      {
        docId: fileId,
        fileField: 'originalFile',
        extraData: {
          orderId: null,
          status: PrintFileStatus.NEW,
          substatus: PrintFileSubStatus.NONE,
        },
      },
    );
  }

  /**
   * Triggers server-side file processing (page counting, conversion, thumbnail generation).
   *
   * @param id - The upload document ID.
   * @param printSettings - The print configuration to apply during processing.
   * @param collection - The Firestore collection path (default: 'uploads').
   * @param printAllowed - Whether the user has accepted print-protected documents.
   */
  async processFile(
    id: string,
    printSettings: PrintSettings,
    collection: string = 'uploads',
    printAllowed: boolean = false,
  ): Promise<ProcessFileApiResponse> {
    if (!(id && printSettings)) {
      return Promise.reject(new Error('Missing id or printSettings'));
    }

    const data = {
      collection,
      uploadId: id,
      printSettings,
      ...(printAllowed ? { printAllowed: true } : {}),
    };

    return this.callWithRetry(() => this.uploadApi.processFile(data)).catch(
      (err) => {
        console.error('--- ProcessFile Process ---');
        console.error(err);
        return err;
      }
    );
  }

  /**
   * Triggers server-side thumbnail regeneration for an upload.
   *
   * @param id - The upload document ID.
   * @param printSettings - The print configuration for the thumbnail.
   * @param collection - The Firestore collection path (default: 'uploads').
   * @param skipProductUpdate - Whether to skip updating the parent product.
   */
  async updateThumbnail(
    id: string,
    printSettings: PrintSettings,
    collection: string = 'uploads',
    skipProductUpdate: boolean = false,
  ): Promise<UpdateThumbnailApiResponse> {
    const data = {
      collection,
      uploadId: id,
      printSettings,
      skipProductUpdate,
    };

    return this.callWithRetry(() => this.uploadApi.updateThumbnail(data)).catch(
      (err) => {
        console.error('--- ProcessFile thumbnail ---');
        console.error(err);
        return err;
      }
    );
  }

  /**
   * Returns a real-time Observable of an upload document.
   *
   * @param id - The upload document ID.
   */
  getUpload$(id: string): Observable<PrintFile> {
    return this.uploadDbService.get$(id);
  }

  /**
   * Deletes an upload document from Firestore.
   *
   * @param id - The upload document ID.
   */
  deleteUpload(id: string): Promise<void> {
    return this.uploadDbService.delete(id);
  }

  /**
   * Retries a function up to maxCount times with randomized back-off delays.
   * Mirrors the original `executeWithRetry` from `src/app/shared/utils/execute-helper.ts`.
   */
  private async callWithRetry<T>(fn: () => Promise<T>, maxCount = 3): Promise<T> {
    let lastError: any;
    for (let retry = 0; retry < maxCount; retry++) {
      try {
        if (retry > 0) {
          const delay = retry * (Math.floor(Math.random() * 101) + 100);
          await new Promise(r => setTimeout(r, delay));
        }
        return await fn();
      } catch (err) {
        lastError = err;
        console.log('Error execute Retry: ' + err.message);
      }
    }
    console.error(new Error('ERROR NOTIFY: Execute call retries reached: ' + lastError.message));
    console.error(lastError);
    throw lastError;
  }
}
